import { MAX_RECENT_ITEMS } from '../constants';
import type { RecentContract, RecentSearch } from '../types';

const RECENT_SEARCHES_KEY = 'contract-builder:command-palette:recent-searches';
const RECENT_CONTRACTS_KEY = 'contract-builder:command-palette:recent-contracts';

const readList = <T>(key: string): T[] => {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeList = <T>(key: string, items: T[]): void => {
  try {
    localStorage.setItem(key, JSON.stringify(items));
  } catch {
    // storage full or unavailable — recents are best-effort
  }
};

export const getRecentSearchesFromStorage = (username: string): RecentSearch[] =>
  readList<RecentSearch>(`${username}:${RECENT_SEARCHES_KEY}`);

export const removeRecentSearchFromStorage = (username: string, query: string): void => {
  const trimmed = query.trim();
  if (!trimmed) {
    return;
  }
  const existing = getRecentSearchesFromStorage(username).filter(
    (item) => item.query.toLowerCase() !== trimmed.toLowerCase(),
  );
  writeList(`${username}:${RECENT_SEARCHES_KEY}`, existing);
};

export const addRecentSearchToStorage = (username: string, query: string): void => {
  const trimmed = query.trim();
  if (!trimmed) {
    return;
  }
  const existing = getRecentSearchesFromStorage(username).filter(
    (item) => item.query.toLowerCase() !== trimmed.toLowerCase(),
  );
  writeList(
    `${username}:${RECENT_SEARCHES_KEY}`,
    [{ query: trimmed, visitedAt: Date.now() }, ...existing].slice(0, MAX_RECENT_ITEMS),
  );
};

export const removeRecentContractFromStorage = (username: string, id: string): void => {
  const existing = getRecentContractsFromStorage(username).filter((item) => item.id !== id);
  writeList(`${username}:${RECENT_CONTRACTS_KEY}`, existing);
};

export const getRecentContractsFromStorage = (username: string): RecentContract[] =>
  readList<RecentContract>(`${username}:${RECENT_CONTRACTS_KEY}`);

export const addRecentContractToStorage = (username: string, contract: Omit<RecentContract, 'visitedAt'>): void => {
  if (!contract.id || !contract.name) {
    return;
  }
  const existing = getRecentContractsFromStorage(username).filter((item) => item.id !== contract.id);
  writeList(
    `${username}:${RECENT_CONTRACTS_KEY}`,
    [{ ...contract, visitedAt: Date.now() }, ...existing].slice(0, MAX_RECENT_ITEMS),
  );
};
